import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle as CheckIcon,
  Handshake as ServicesIcon,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * WelcomeModal Component
 * Shows a welcome message to newly confirmed users
 * Provides confirmation that their account is active and ready to use
 */
const WelcomeModal = ({ isOpen, onClose, userName, centerName }) => {
  if (!isOpen) return null;

  const firstName = userName ? userName.split(' ')[0] : 'there';

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: -10,
      transition: {
        duration: 0.2
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 }
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2 }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="welcome-modal-overlay"
            onClick={onClose}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={overlayVariants}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
          >
            {/* Modal Content */}
            <motion.div
              className="welcome-modal-content"
              onClick={(e) => e.stopPropagation()}
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '450px',
                width: '100%',
                position: 'relative',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                textAlign: 'center'
              }}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                  e.currentTarget.style.color = '#6B7280';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9CA3AF';
                }}
              >
                <CloseIcon style={{ fontSize: '24px' }} />
              </button>

              {/* Success Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 200,
                  damping: 15
                }}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}
              >
                <CheckIcon style={{
                  fontSize: '48px',
                  color: 'white'
                }} />
              </motion.div>

              {/* Welcome Message */}
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#1F2937',
                marginBottom: '12px'
              }}>
                Welcome, {firstName}!
              </h2>

              <p style={{
                fontSize: '16px',
                color: '#6B7280',
                marginBottom: '24px',
                lineHeight: '1.5'
              }}>
                Your account has been verified successfully.
                You're now part of the {centerName || 'community'}.
              </p>

              {/* Services Info */}
              <div style={{
                backgroundColor: '#F9FAFB',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <ServicesIcon style={{
                    fontSize: '24px',
                    color: '#10B981'
                  }} />
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1F2937',
                    margin: 0
                  }}>
                    Community Services
                  </h3>
                </div>

                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  You can now post services you need or offer services to help others in your community.
                  Click the green button to get started!
                </p>
              </div>

              {/* Get Started Button */}
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#059669';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#10B981';
                }}
              >
                Get Started
              </motion.button>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeModal;